import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../../app/store';
import { lookupLogin } from '../../api/hattersAPI';
import { isEmail } from '../../utils/Validations';

export interface ILookup {
  hatName?: string;
  hatCluster?: string;
  verified?: boolean;
}

export enum LoginState {
  email = 'login/email',
  username = 'login/username',
}

interface EmailLogin {
  email: string;
  isValid: boolean;
}

interface UsernameLogin {
  username: string;
  isValid: boolean;
}

interface ErrorLogin {
  displayError: boolean;
  errorType: string;
}

interface ILoginState {
  lookupResponse: ILookup;
  loginState: LoginState;
  email: EmailLogin;
  username: UsernameLogin;
  error: ErrorLogin;
  processing: boolean;
}

const initialState: ILoginState = {
  loginState: LoginState.username,
  lookupResponse: {},
  email: { email: '', isValid: false },
  username: { username: '', isValid: false },
  error: { displayError: false, errorType: '' },
  processing: false,
};

export const slice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    changeLoginState: (state, action: PayloadAction<LoginState>) => {
      state.loginState = action.payload;
    },
    isProcessing: (state, action: PayloadAction<boolean>) => {
      state.processing = action.payload;
    },
    updateEmail: (state, action: PayloadAction<EmailLogin>) => {
      Object.assign(state.email, action.payload);
    },
    updateUsername: (state, action: PayloadAction<UsernameLogin>) => {
      Object.assign(state.username, action.payload);
    },
    updateError: (state, action: PayloadAction<ErrorLogin>) => {
      Object.assign(state.error, action.payload);
      state.processing = false;
    },
    updateLookupResponse: (state, action: PayloadAction<ILookup>) => {
      Object.assign(state.lookupResponse, action.payload);
    },
  },
});

export const { changeLoginState, isProcessing, updateEmail, updateUsername, updateError, updateLookupResponse } =
  slice.actions;

export const setLoginState =
  (state: LoginState): AppThunk =>
    (dispatch) => {
      dispatch(changeLoginState(state));
    };
export const setLoginProcessing =
  (flag: boolean): AppThunk =>
    (dispatch) => {
      dispatch(isProcessing(flag));
    };
export const setLoginEmail =
  (email: EmailLogin): AppThunk =>
    (dispatch) => {
      dispatch(updateEmail(email));
    };
export const setLoginUsername =
  (username: UsernameLogin): AppThunk =>
    (dispatch) => {
      dispatch(updateUsername(username));
    };
export const setLoginError =
  (error: ErrorLogin): AppThunk =>
    (dispatch) => {
      dispatch(updateError(error));
    };
export const setLookupResponse =
  (lookup: ILookup): AppThunk =>
    (dispatch) => {
      dispatch(updateLookupResponse(lookup));
    };

export const selectLoginEmail = (state: RootState) => state.login.email;
export const selectLoginUsername = (state: RootState) => state.login.username;
export const selectLoginError = (state: RootState) => state.login.error;
export const selectLoginProcessing = (state: RootState) => state.login.processing;
export const selectLoginCurrentState = (state: RootState) => state.login.loginState;
export const selectLoginState = (state: RootState) => state.login;
export const selectLookupResponse = (state: RootState) => state.login.lookupResponse;

export const login =
  (username: string, sandbox: boolean): AppThunk =>
    async (dispatch) => {
      dispatch(setLoginProcessing(true));

      try {
        const res = await lookupLogin(username, sandbox);
        if (res.parsedBody) {
          dispatch(setLookupResponse(res.parsedBody));
        }
      } catch (e) {
        const errorType = isEmail(username) ? 'emailNotRecognised' : 'usernameNotRecognised';
        dispatch(setLoginError({ displayError: true, errorType: errorType }));
      }
    };

export default slice.reducer;
